import React, { useState } from 'react';
import styled from 'styled-components';
import TrueImpactSimpleModal from 'shared/design-system/components/TrueImpactSimpleModal';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

const ModalContent = styled.div`
  padding: ${SPACERS.medium};
  min-width: 400px;

  .modal-title {
    margin-bottom: ${SPACERS.small};
  }

  .modal-description {
    margin-bottom: ${SPACERS.medium};
    color: ${COLORS.grayDark};
  }

  .input-wrapper {
    margin-bottom: ${SPACERS.medium};

    label {
      display: block;
      margin-bottom: ${SPACERS.extraTiny};
      font-weight: 600;
      color: ${COLORS.grayDarkest};
    }

    input {
      width: 100%;
      padding: ${SPACERS.tiny};
      border: 1px solid ${COLORS.grayLight};
      border-radius: 4px;
      font-size: 14px;
      font-family: inherit;

      &:focus {
        outline: none;
        border-color: ${COLORS.ctaPrimary};
      }

      &::placeholder {
        color: ${COLORS.grayBase};
      }
    }
  }
`;

interface EditReportNameModalProps {
  open: boolean;
  currentName: string;
  onClose: () => void;
  onSave: (newName: string) => Promise<void>;
}

const EditReportNameModal: React.FC<EditReportNameModalProps> = ({
  open,
  currentName,
  onClose,
  onSave,
}) => {
  const [reportName, setReportName] = useState(currentName);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!reportName.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(reportName.trim());
      onClose();
    } catch (error) {
      console.error('Error saving report name:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setReportName(currentName);
    onClose();
  };

  return (
    <TrueImpactSimpleModal
      open={open}
      onClose={handleClose}
      next={{
        text: 'Save',
        onClick: handleSave,
        disabled: !reportName.trim() || reportName.trim() === currentName,
        isLoading: isSaving,
      }}
      previous={{
        text: 'Cancel',
        onClick: handleClose,
        disabled: isSaving,
      }}
    >
      <ModalContent>
        <TrueImpactText variant="heading" className="modal-title">
          Edit Report Name
        </TrueImpactText>
        <TrueImpactText variant="body" className="modal-description">
          Update the name of your report.
        </TrueImpactText>
        <div className="input-wrapper">
          <input
            id="report-name"
            type="text"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            placeholder="Enter report name"
            autoFocus
            disabled={isSaving}
          />
        </div>
      </ModalContent>
    </TrueImpactSimpleModal>
  );
};

export default EditReportNameModal;
